import React from "react";

export type Status = "on-time" | "not-on-time" | "not-logged" | "skipped";

export interface MealCellInfo {
  status: Status;
  mealPlanType?: string;
  plannedTime?: string; // formatted, e.g. "8:00 am"
}

export interface MealRow {
  meal: string;
  time: string;
  cells: MealCellInfo[]; // one per day (Mo..Su)
}

interface MealTimingConsistencyProps {
  data?: MealRow[];
  unplanned?: number[];
}

const DAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const TEAL = "#0DB5B5";
const ORANGE = "#FF9500";
const SKIP_BG = "#FFE5E4";
const SKIP_BORDER = "#C72E25";
const NOT_LOGGED_BG = "#F0F0F0";

const LEGEND: { label: string; type: Status }[] = [
  { label: "On time", type: "on-time" },
  { label: "Not on time", type: "not-on-time" },
  { label: "Not logged", type: "not-logged" },
  { label: "Skipped", type: "skipped" },
];

const LegendDot: React.FC<{ type: Status }> = ({ type }) => {
  const base = "w-2.5 h-2.5 rounded-full inline-block";
  if (type === "on-time") return <span className={base} style={{ background: TEAL }} />;
  if (type === "not-on-time") return <span className={base} style={{ background: ORANGE }} />;
  if (type === "not-logged") return <span className={base} style={{ background: NOT_LOGGED_BG }} />;
  return (
    <span
      className={base}
      style={{ background: SKIP_BG, border: `0.5px dashed ${SKIP_BORDER}` }}
    />
  );
};

const StatusCell: React.FC<{ cell: MealCellInfo }> = ({ cell }) => {
  const base = "h-6 w-full rounded-[6px]";
  const { status, mealPlanType, plannedTime } = cell;

  const bar =
    status === "on-time" ? (
      <div className={base} style={{ background: TEAL }} />
    ) : status === "not-on-time" ? (
      <div className={base} style={{ background: ORANGE }} />
    ) : status === "not-logged" ? (
      <div className={base} style={{ background: NOT_LOGGED_BG }} />
    ) : (
      <div
        className={base}
        style={{ background: SKIP_BG, border: `0.5px dashed ${SKIP_BORDER}` }}
      />
    );

  const showTooltip = !!(mealPlanType || plannedTime);
  if (!showTooltip) return bar;

  return (
    <div className="relative group w-full">
      {bar}
      <div
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <div className="bg-white border border-[#E5E7EB] rounded-[8px] px-3 py-2 shadow-md text-xs font-inter whitespace-nowrap">
          {mealPlanType && (
            <p className="font-semibold text-[#1F1F1F] mb-0.5 text-[11px]">
              {mealPlanType}
            </p>
          )}
          {plannedTime && (
            <p className="text-[11px] text-[#8E8E93]">
              Meal time: <span className="text-[#1F1F1F] font-medium">{plannedTime}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const MealTimingConsistency: React.FC<MealTimingConsistencyProps> = ({
  data = [],
  unplanned = [0, 0, 0, 0, 0, 0, 0],
}) => {
  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          Meal Timing Consistency
        </span>
        <div className="flex items-center gap-3">
          {LEGEND.map((l) => (
            <div key={l.label} className="flex items-center gap-1.5">
              <LegendDot type={l.type} />
              <span className="text-[11px] 3xl:text-[12px] text-[#1F1F1F] font-inter">
                {l.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Grid: label column + 7 day columns */}
      <div className="flex flex-col gap-1.5">
        {data.map((row) => (
          <div
            key={row.meal}
            className="grid gap-1.5 items-center"
            style={{ gridTemplateColumns: "80px repeat(7, 1fr)" }}
          >
            <div className="text-[11px] 3xl:text-[12px] text-[#1F1F1F] font-inter">
              <span className="font-medium">{row.meal}</span>
            </div>
            {row.cells.map((c, i) => (
              <StatusCell key={i} cell={c} />
            ))}
          </div>
        ))}

        {/* Day labels */}
        <div
          className="grid gap-1.5 items-center mt-1"
          style={{ gridTemplateColumns: "80px repeat(7, 1fr)" }}
        >
          <div />
          {DAYS.map((d) => (
            <div
              key={d}
              className="text-[11px] 3xl:text-[12px] text-[#8D8D8D] font-inter text-center"
            >
              {d}
            </div>
          ))}
        </div>

        {/* Unplanned row */}
        <div
          className="grid gap-1.5 items-center mt-2 pt-2 border-t border-[#F3F4F6]"
          style={{ gridTemplateColumns: "80px repeat(7, 1fr)" }}
        >
          <div className="text-[11px] 3xl:text-[12px] font-medium text-[#B77BFA] font-inter">
            Unplanned
          </div>
          {unplanned.map((n, i) => (
            <div
              key={i}
              className="text-[12px] 3xl:text-[13px] font-semibold text-[#1F1F1F] font-inter text-center"
            >
              {n}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MealTimingConsistency;
